/**
 * Template Preview Component
 * 
 * Shows a read-only preview of a template structure with cards and fields.
 * Used in Template Builder and Apply Template modals.
 */

import React, { useMemo } from 'react';
import type { Template, CatalogField } from '../types/models';

interface TemplatePreviewProps {
  template: Template;
  catalogFields: CatalogField[];
  onClose?: () => void;
  showModal?: boolean;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  catalogFields,
  onClose,
  showModal = true,
}) => {
  // Create a map of field IDs to catalog fields
  const catalogMap = useMemo(() => {
    return new Map(catalogFields.map(f => [f.fieldId, f]));
  }, [catalogFields]);

  // Enrich template fields with catalog data
  const enrichedFields = useMemo(() => {
    return template.fields
      .map(tf => ({
        ...tf,
        catalog: catalogMap.get(tf.fieldId),
      }))
      .filter(f => f.catalog); // Only include fields that exist in catalog
  }, [template.fields, catalogMap]);

  // Group fields by card
  const fieldsByCard = useMemo(() => {
    const grouped = new Map<number, typeof enrichedFields>();
    enrichedFields.forEach(field => {
      if (!grouped.has(field.cardNumber)) {
        grouped.set(field.cardNumber, []);
      }
      grouped.get(field.cardNumber)!.push(field);
    });
    // Sort by displayOrder within each card
    grouped.forEach(fields => {
      fields.sort((a, b) => a.displayOrder - b.displayOrder);
    });
    return grouped;
  }, [enrichedFields]);

  // Get sorted card entries
  const sortedCards = useMemo(() => {
    return Object.entries(template.cards).sort(([a], [b]) => parseInt(a) - parseInt(b));
  }, [template.cards]);

  const content = (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h4 className="mb-1">{template.displayName}</h4>
        {template.description && (
          <p className="np-sub mb-2">{template.description}</p>
        )}
        <div>
          <span className="np-chip np-tone-primary me-2">
            {template.category}
          </span>
          <span className="np-chip np-tone-info me-2 np-tabular">
            {template.fields.length} fields
          </span>
          <span className="np-chip np-tone-neutral np-tabular">
            {Object.keys(template.cards).length} cards
          </span>
        </div>
      </div>

      {/* Cards and Fields */}
      {sortedCards.length === 0 ? (
        <div className="np-banner np-tone-info">
          <i className="ri-information-line"></i>
          <div>This is a blank template with no predefined fields or cards.</div>
        </div>
      ) : (
        <div className="row">
          {sortedCards.map(([cardNum, cardName]) => {
            const fieldsInCard = fieldsByCard.get(parseInt(cardNum)) || [];
            return (
              <div key={cardNum} className="col-md-6 mb-3">
                <div className="np-card h-100">
                  <div className="np-card-head">
                    <span className="np-step-badge np-tabular">{cardNum}</span>
                    <h2>{cardName}</h2>
                    <span className="np-chip np-tone-neutral np-tabular">
                      {fieldsInCard.length} fields
                    </span>
                  </div>
                  <div className="np-card-body">
                    {fieldsInCard.length === 0 ? (
                      <p className="np-sub"><i className="ri-list-check-2 me-1"></i>No fields assigned</p>
                    ) : (
                      <div className="list-group list-group-flush">
                        {fieldsInCard.map((field, index) => (
                          <div key={field.fieldId} className="list-group-item px-0 pt-0">
                            <div className="d-flex align-items-start">
                              <span className="np-sub me-2 np-tabular">{index + 1}.</span>
                              <div className="flex-grow-1">
                                <div className="fw-medium small">
                                  {field.catalog?.fieldName}
                                  {field.catalog?.isRequired && (
                                    <span className="text-danger ms-1">*</span>
                                  )}
                                </div>
                                <div className="np-sub" style={{ fontSize: '0.75rem' }}>
                                  <code>{field.fieldId}</code>
                                  <span className="mx-1">•</span>
                                  <span className="np-chip np-tone-neutral">
                                    {field.catalog?.fieldType}
                                  </span>
                                  {field.catalog?.isCalculated && (
                                    <>
                                      <span className="mx-1">•</span>
                                      <span className="np-chip np-tone-warning">calculated</span>
                                    </>
                                  )}
                                  {field.catalog?.isSystemField && (
                                    <>
                                      <span className="mx-1">•</span>
                                      <span className="np-chip np-tone-info">system</span>
                                    </>
                                  )}
                                </div>
                                {field.catalog?.helpText && (
                                  <div className="np-sub mt-1" style={{ fontSize: '0.75rem' }}>
                                    {field.catalog.helpText}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="np-card mt-3">
        <div className="np-card-head">
          <h2>Summary</h2>
        </div>
        <div className="np-card-body">
          <div className="row">
            <div className="col-6">
              <div className="small np-sub">Total Fields</div>
              <div className="h5 mb-0 np-tabular">{enrichedFields.length}</div>
            </div>
            <div className="col-6">
              <div className="small np-sub">Total Cards</div>
              <div className="h5 mb-0 np-tabular">{sortedCards.length}</div>
            </div>
          </div>
          {enrichedFields.length > 0 && (
            <div className="mt-3">
              <div className="small np-sub mb-1">Field Types</div>
              <div>
                {Array.from(
                  enrichedFields.reduce((acc, f) => {
                    const type = f.catalog?.fieldType || 'unknown';
                    acc.set(type, (acc.get(type) || 0) + 1);
                    return acc;
                  }, new Map<string, number>())
                ).map(([type, count]) => (
                  <span key={type} className="np-chip np-tone-neutral me-1 np-tabular">
                    {type}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!showModal) {
    return content;
  }

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ri-eye-line me-2"></i>
              Template Preview
            </h5>
            {onClose && (
              <button type="button" className="btn-close" onClick={onClose}></button>
            )}
          </div>
          <div className="modal-body">
            {content}
          </div>
          {onClose && (
            <div className="modal-footer">
              <button type="button" className="np-btn np-btn-ghost" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
